import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import Loading from '@/components/Loading';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import type AgentApiAiRecommendationReferenceItemsIndex from '@/types/serializers/AgentApiAiRecommendationReferenceItemsIndex';
import type { ReferenceSelection } from '../types/referenceSelection';
import formatInterviewedAt from '../lib/formatInterviewedAt';

type ActiveGroup = 'candidate' | 'interview';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: AgentApiAiRecommendationReferenceItemsIndex | undefined;
  isLoading: boolean;
  value: ReferenceSelection;
  onConfirm: (next: ReferenceSelection) => void;
};

type SelectListItemProps = {
  label: string;
  selected: boolean;
  onClick: () => void;
};

function SelectListItem({ label, selected, onClick }: SelectListItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center px-sm py-xs text-left text-body-sm',
        selected ? 'bg-primary_lighten text-primary' : 'text-main hover:bg-gray'
      )}
    >
      {label}
    </button>
  );
}

/**
 * 参照情報を選択するモーダル
 *
 * 3カラム構成: グループ（候補者情報 / 面談情報）→ カテゴリ → 項目（チェックボックス）
 * 内部の作業用選択状態を value から初期化し、「決定」でのみ onConfirm にコミットする。
 */
export default function ReferenceItemsPickerDialog({
  open,
  onOpenChange,
  data,
  isLoading,
  value,
  onConfirm,
}: Props) {
  const [activeGroup, setActiveGroup] = useState<ActiveGroup>('candidate');
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [customFieldIds, setCustomFieldIds] = useState<number[]>([]);
  const [interviewLogIds, setInterviewLogIds] = useState<number[]>([]);

  const categories = data?.candidate_info_categories ?? [];
  const interviewLogs = data?.interview_logs ?? [];

  // モーダルを開くたびに value から作業状態を初期化する
  useEffect(() => {
    if (open) {
      setCustomFieldIds(value.customFieldIds);
      setInterviewLogIds(value.interviewLogIds);
      setActiveGroup('candidate');
      setActiveCategoryId(null);
    }
  }, [open, value]);

  // カテゴリ取得後に未選択ならば先頭カテゴリをアクティブにする
  useEffect(() => {
    if (open && activeCategoryId === null && categories.length > 0) {
      setActiveCategoryId(categories[0].id);
    }
  }, [open, activeCategoryId, categories]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === activeCategoryId) ?? null,
    [categories, activeCategoryId]
  );

  const customFieldIdSet = useMemo(() => new Set(customFieldIds), [customFieldIds]);
  const interviewLogIdSet = useMemo(() => new Set(interviewLogIds), [interviewLogIds]);

  const toggleCustomField = (id: number) => {
    setCustomFieldIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleInterviewLog = (id: number) => {
    setInterviewLogIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 現在のカテゴリ内の全フィールドをまとめてトグルする
  const categoryFieldIds = activeCategory?.custom_fields.map((f) => f.id) ?? [];
  const allCategoryChecked =
    categoryFieldIds.length > 0 && categoryFieldIds.every((id) => customFieldIdSet.has(id));

  const toggleAllCategory = () => {
    setCustomFieldIds((prev) => {
      const rest = prev.filter((id) => !categoryFieldIds.includes(id));
      return allCategoryChecked ? rest : [...rest, ...categoryFieldIds];
    });
  };

  const handleConfirm = () => {
    onConfirm({ customFieldIds, interviewLogIds });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-[643px] max-w-[95vw] flex-col rounded-sm p-0 shadow-layer3">
        {/* ヘッダー */}
        <div className="flex h-[56px] shrink-0 items-center border-b border-solid border-gray-200 px-lg">
          <DialogTitle className="text-base font-bold text-main">
            参照情報を選択してください
          </DialogTitle>
        </div>

        {/* ボディ: 3カラムセレクタ（ヘッダー・フッターと余白なしで接続し、縦区切り線を上下に貫通させる） */}
        <div className="flex h-[322px] bg-white">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Loading size="small" />
            </div>
          ) : (
            <>
              {/* カラム1: グループ */}
              <div className="flex w-[180px] shrink-0 flex-col overflow-y-auto border-r border-solid border-border p-2">
                <SelectListItem
                  label="候補者情報"
                  selected={activeGroup === 'candidate'}
                  onClick={() => setActiveGroup('candidate')}
                />
                <SelectListItem
                  label="面談情報"
                  selected={activeGroup === 'interview'}
                  onClick={() => setActiveGroup('interview')}
                />
              </div>

              {/* カラム2: カテゴリ（候補者情報のときのみ） */}
              {activeGroup === 'candidate' && (
                <div className="flex w-[210px] shrink-0 flex-col overflow-y-auto p-2">
                  {categories.map((category) => (
                    <SelectListItem
                      key={category.id}
                      label={category.name}
                      selected={category.id === activeCategoryId}
                      onClick={() => setActiveCategoryId(category.id)}
                    />
                  ))}
                </div>
              )}

              {/* カラム3: 項目（チェックボックス） */}
              <div className="flex min-w-[253px] flex-1 flex-col overflow-y-auto border-l border-solid border-border p-xs">
                {(
                  activeGroup === 'candidate'
                    ? categoryFieldIds.length === 0
                    : interviewLogs.length === 0
                ) ? (
                  <div className="flex flex-1 items-start justify-center py-x3l">
                    <span className="text-body-xs text-sub">選択可能な項目がありません</span>
                  </div>
                ) : activeGroup === 'candidate' ? (
                  <>
                    {categoryFieldIds.length > 0 && (
                      <div className="px-sm py-xs">
                        <Checkbox checked={allCategoryChecked} onChange={toggleAllCategory}>
                          <span className="text-body-sm text-main">全て選択</span>
                        </Checkbox>
                      </div>
                    )}
                    {activeCategory?.custom_fields.map((field) => (
                      <div key={field.id} className="px-sm py-xs">
                        <Checkbox
                          checked={customFieldIdSet.has(field.id)}
                          onChange={() => toggleCustomField(field.id)}
                        >
                          <span className="text-body-sm text-main">{field.name}</span>
                        </Checkbox>
                      </div>
                    ))}
                  </>
                ) : (
                  interviewLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between gap-2 px-sm py-xs"
                    >
                      <div className="min-w-0 flex-1">
                        <Checkbox
                          checked={interviewLogIdSet.has(log.id)}
                          onChange={() => toggleInterviewLog(log.id)}
                        >
                          <span className="flex min-w-0 items-center gap-[10px]">
                            {log.interviewer_name && (
                              <span className="inline-flex h-6 shrink-0 items-center rounded-full border border-solid border-border bg-white px-sm text-tag-sm text-main">
                                {log.interviewer_name}
                              </span>
                            )}
                            <span className="min-w-0 truncate text-body-xs text-main">
                              {log.title}
                            </span>
                          </span>
                        </Checkbox>
                      </div>
                      <span className="shrink-0 text-body-xs text-placeholder">
                        {formatInterviewedAt(log.interviewed_at)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* フッター */}
        <div className="flex h-[74px] shrink-0 items-center justify-end gap-xs border-t border-solid border-gray-200 px-lg">
          <Button type="button" color="secondary" size="md" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button type="button" color="primary" size="md" onClick={handleConfirm}>
            決定
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
